import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle, WarningCircle, ShieldCheck } from "@phosphor-icons/react";
import { Wordmark } from "../components/Logo";
import { Spinner } from "../components/ui-bits";
import { api } from "../lib/api";

export default function TransferAccept() {
  const { token } = useParams();
  const [transfer, setTransfer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/transfer/${token}`);
        setTransfer(data);
        setEmail(data.toEmail || "");
      } catch {
        setTransfer(null);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const accept = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return toast.error("Fill in all fields");
    setSubmitting(true);
    try {
      await api.post(`/transfer/accept/${token}`, { new_owner_name: name.trim(), new_owner_email: email.trim() });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not accept transfer");
    } finally {
      setSubmitting(false);
    }
  };

  const bad = !loading && (!transfer || transfer.status !== "pending");

  return (
    <div className="min-h-screen bg-navy flex items-center justify-center px-6 py-10">
      <div className="w-full max-w-md bg-white rounded-3xl shadow-luxe-lg p-9 animate-fade-up">
        <div className="flex justify-center mb-6"><Wordmark /></div>

        {loading ? (
          <div className="py-10 flex justify-center"><Spinner className="h-10 w-10" /></div>
        ) : bad ? (
          <div className="text-center py-6">
            <WarningCircle size={52} weight="light" className="mx-auto text-score-red" />
            <h1 className="text-2xl text-navy mt-4">Transfer Unavailable</h1>
            <p className="text-navy/50 text-sm mt-2">
              {transfer ? `This transfer is ${transfer.status}.` : "This transfer link is invalid."}
            </p>
          </div>
        ) : done ? (
          <div className="text-center py-6" data-testid="accept-success">
            <CheckCircle size={56} weight="fill" className="mx-auto text-score-green" />
            <h1 className="text-2xl text-navy mt-4">Ownership Confirmed</h1>
            <p className="text-navy/50 text-sm mt-2">You are now the verified owner of this item. Its passport has been updated.</p>
            <a href={`/verify/${transfer.certificateId}`} className="mt-6 inline-block text-sm text-navy underline hover:text-gold transition-colors">View your passport</a>
          </div>
        ) : (
          <form onSubmit={accept} data-testid="accept-form">
            <div className="text-center mb-6">
              <div className="h-16 w-16 rounded-2xl bg-cream overflow-hidden mx-auto mb-4">
                {transfer.product.image ? <img src={transfer.product.image} alt="" className="w-full h-full object-cover" /> : null}
              </div>
              <span className="flex items-center justify-center gap-1.5 text-[0.62rem] uppercase tracking-[0.2em] text-gold"><ShieldCheck size={14} weight="fill" /> Ownership Transfer</span>
              <h1 className="text-2xl text-navy mt-2 leading-tight">{transfer.product.name}</h1>
              <p className="text-navy/50 text-sm">{transfer.product.brand}</p>
            </div>

            <label className="text-[0.62rem] uppercase tracking-[0.2em] text-navy/50">Your Name</label>
            <input data-testid="accept-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Full name" className="mt-2 mb-4 w-full bg-cream border border-navy/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-gold transition-colors text-navy" />

            <label className="text-[0.62rem] uppercase tracking-[0.2em] text-navy/50">Your Email</label>
            <input data-testid="accept-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2 mb-6 w-full bg-cream border border-navy/10 rounded-xl px-4 py-3 text-sm outline-none focus:border-gold transition-colors text-navy" />

            <button data-testid="accept-submit-button" disabled={submitting} className="w-full flex items-center justify-center gap-2 bg-gold text-navy font-medium rounded-xl py-4 text-sm hover:brightness-105 active:scale-[0.99] transition-all disabled:opacity-60">
              {submitting ? <><Spinner className="h-4 w-4" color="#1B2B5E" /> Confirming…</> : "Accept Ownership"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
